import { Eye, EyeOff } from 'lucide-react';
import { forwardRef, useId, useState } from 'react';
import { cn } from '../../lib/cn.js';
import { Input, type InputProps } from './input.js';

export interface PasswordInputProps extends Omit<InputProps, 'type'> {
  /** Announced when the password is hidden, on the control that reveals it. */
  showLabel?: string;
  /** Announced when it is showing. */
  hideLabel?: string;
}

/**
 * A password field you can look at.
 *
 * Typing a long passphrase blind — which is what a twelve-character
 * minimum encourages — is how people end up choosing short ones, or
 * pasting from somewhere worse. So the field can be revealed, and starts
 * hidden because the common case is somebody signing in with other people
 * in the room.
 *
 * It stays a single `<input>` whose `type` flips, rather than two swapped
 * fields: password managers and autofill key off the element, and
 * replacing it on every toggle loses the value and the association.
 *
 * The labels are props rather than translated here, as everything else in
 * this package does it — `@zuruflex/ui` has no i18n of its own, so a caller
 * hands it the words in the reader's language.
 */
export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  function PasswordInput(
    { className, showLabel = 'Show password', hideLabel = 'Hide password', ...props },
    ref,
  ) {
    const [visible, setVisible] = useState(false);
    const describedBy = useId();

    return (
      <div className="relative">
        <Input
          ref={ref}
          type={visible ? 'text' : 'password'}
          // Room for the control, so a long passphrase does not run
          // underneath it.
          className={cn('pr-11', className)}
          {...props}
        />

        <button
          type="button"
          // Never a submit: this sits inside a form, and the default type
          // would make peeking at your password send it.
          onClick={() => setVisible((showing) => !showing)}
          disabled={props.disabled}
          aria-label={visible ? hideLabel : showLabel}
          aria-pressed={visible}
          aria-controls={props.id ?? describedBy}
          className={cn(
            'absolute inset-y-0 right-0 grid w-11 place-items-center rounded-r-field',
            'text-text-subtle transition-colors hover:text-text',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
            'disabled:pointer-events-none disabled:opacity-60',
          )}
        >
          {/* Decorative: the button already has an accessible name, and a
              second announcement of "eye" helps nobody. */}
          {visible ? (
            <EyeOff className="size-4" aria-hidden="true" />
          ) : (
            <Eye className="size-4" aria-hidden="true" />
          )}
        </button>
      </div>
    );
  },
);
